import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import { uniq, filter } from 'ramda';
import { NumericFormat } from 'react-number-format';
import PinIcon from '../../components/Icons/PinIcon';
import DownPinIcon from '../../components/Icons/DownPinIcon';
import TextInput from '../../components/TextInput';
import { useTargetsRequest, useUpdatePosPeriodRequest } from "../../hooks/useApi";
import { creatTargetSalesFormData, CURRENCY_MAP, deepMerge, getMonths, MONTH_MAP, targetSalesTotals, targetSalesAverage, targetSalesTotalsV3, targetSalesAverageV3, currencyToNumber } from '../../utils/helpers';
import { FormatMoney, FormatNumberToOneDigit, FormatNumberToTwoDigits } from '../../utils/number-formats';
import debounce from 'lodash.debounce';
import './index.css';


const TargetTable = ({ targetData, selectedYear, handelChangePosId, getBodyParams }) => {
  const [formData, setFormData] = useState([]);
  const [ {}, updatePosPeriod ] = useUpdatePosPeriodRequest();
  const [ { loading }, getTotalsData ] = useTargetsRequest();
  const [stickTotal, setStickTotal] = useState(false);
  const [tTargetData, setTargetData] = useState(targetData);
  const rows = tTargetData?.elements;
  const posIdsToChange = useRef([]);
  const debouncedSave = useCallback(
    debounce(async(event, mergedObj) => {
      const { target: { value, name } } = event;
      const posIds = uniq(posIdsToChange.current);
      var promises = posIds.map(async item => {
        const posId = item.split('-')[0];
        const month = item.split('-')[1];
        const period = item.split('-')[2];
        const bodyParams = {
          year: parseInt(selectedYear),
          month: parseInt(month),
          period: parseInt(period),
          posId,
          budget: parseInt(mergedObj[posId][period].budget) ?? 0,
        };
        await updatePosPeriod({ bodyParams });
      });
      Promise.all(promises).then(async()=> {
        posIdsToChange.current = [];
        const resp = await getTotalsData({ bodyParams: getBodyParams });
        setTargetData(resp?.data);
      });
      
    }, 1500),
    [], // will be created only once initially
  );
  
  const handleChange = async(event) => {
    const { target: { value, name } } = event;
    const posId = event.target.getAttribute('data-posid');
    const month = event.target.getAttribute('data-month');
    const period = event.target.getAttribute('data-period');
    const fieldName = event.target.getAttribute('data-fieldname');
    const newObj = {
      [posId]: {
        [period]: {
          [fieldName]: currencyToNumber(value || '0')
        }
      }
    };
    const mergedObj = deepMerge(formData, newObj);
    setFormData(mergedObj);
    debouncedSave.cancel();
    const newElement = `${posId}-${month}-${period}`;
    posIdsToChange.current.push(newElement);
    debouncedSave(event, mergedObj, posIdsToChange);
  };
  
  useEffect(() => {
    setFormData(creatTargetSalesFormData(rows));
  }, [tTargetData]);

  if (formData == [])
    return <>LOADING</>
  if (!rows)
    return <>LOADING</>
  if (rows.length <= 0)
    return <>LOADING</>

  const monthsHeader = getMonths(rows[0]?.monthValues);
  const globalCurrencyId = CURRENCY_MAP[rows[0]?.currency] ? CURRENCY_MAP[rows[0]?.currency] : rows[0]?.currency;
  return (
    <div className='etroContainer targetSalesTableWrapper'>
      <table id="scrollableTable" className='tableBorderColor'>
        <thead>
          <tr className='bgTableGrey'>
            <th className='thinText sticky sticky1'>Action</th>
            <th className='thinText sticky sticky_2_1' colSpan="2">Store</th>
            <th colSpan="7" className={stickTotal ? 'divideLeft divideRight sticky sticky_3_6' : 'divideLeft divideRight'}>
              <span className="flex justify-between">
                <span>Total</span>
                <span className="pinIcon" onClick={()=>setStickTotal(!stickTotal)}>
                  {stickTotal && (<DownPinIcon />)}
                  {!stickTotal && (<PinIcon />)}
                </span>
              </span>
            </th>
            {
              monthsHeader.map((month) => {
                return (
                  <th className='divideRight' colSpan="6">{MONTH_MAP[month]}</th>
                );              
              })
            }
          </tr>
          <tr className='bgTableGrey'>
            <th className="sticky sticky_row_2 sticky_row_2_col_1"><input type="checkbox" /></th>
            <th className='thinText sticky sticky_row_2_col_2 sticky_row_2'>Nation</th>
            <th className='thinText sticky sticky_row_2_col_3 sticky_row_2' style={{minWidth: '140px'}}> </th>
            <th className={stickTotal ? 'divideLeft sticky_row_2 sticky sticky_row_2_col_4' : 'divideLeft sticky_row_2'} style={{minWidth: '100px'}}>Budget</th>
            <th className={stickTotal ? 'sticky_row_2 sticky sticky_row_2_col_5' : 'sticky_row_2'}>Actual Sales</th>
            <th className={stickTotal ? 'sticky_row_2 sticky sticky_row_2_col_6' : 'sticky_row_2'}>UPT</th>
            <th className={stickTotal ? 'sticky_row_2 sticky sticky_row_2_col_7': 'sticky_row_2'}>Av. Price</th>
            <th className={stickTotal ? 'sticky_row_2 sticky sticky_row_2_col_8' : 'sticky_row_2'}>#Transaction</th>
            <th className={stickTotal ? 'sticky_row_2 sticky sticky_row_2_col_9' : 'sticky_row_2'}>ATV</th>
            <th className={stickTotal ? 'sticky_row_2 divideRight sticky sticky_row_2_col_10' : 'sticky_row_2 divideRight'}>KPI Buget</th>
            {
              monthsHeader.map((mh)=>{
                return (
                  <>
                    <th className="sticky_row_2">Weeks</th>
                    <th className="sticky_row_2" style={{minWidth: '140px'}}>Budget</th>
                    <th className="sticky_row_2">Actual Sales</th>
                    <th className="sticky_row_2 textRight" style={{minWidth: '140px'}}>&#916; Value</th>
                    <th className="sticky_row_2 textRight">&#916; %</th>
                    <th className='divideRight sticky_row_2'>KPI Buget</th>
                  </>
                );              
              })
            }
          </tr>
        </thead>
        <tbody>
          {
            rows.map( (row) => {
              const months = getMonths(row?.monthValues);
              const currencyId = CURRENCY_MAP[row?.currency] ? CURRENCY_MAP[row?.currency] : row?.currency;
              
              return (
                <>
                  <tr key={row.posId}>
                    <td rowSpan={7} class="sticky sticky1 align-top"><input type="checkbox" value={row.posId} onChange={handelChangePosId} /></td>
                    <td rowSpan={7} className='thinText sticky sticky2 align-top'>{row.countryDs}</td>
                    <td rowSpan={7} className='thinText sticky sticky3 align-top'>{row.posDs}</td>
                    <td rowSpan={7} className={stickTotal ? 'divideLeft textRight sticky sticky4 align-top' : 'divideLeft textRight align-top'}>
                      <div className="noWrap">
                        {<FormatMoney value={row.totalValue?.budget} prefix={currencyId} />}
                      </div>
                      <div className='tdSubBudget noWrap'>
                        {<FormatMoney value={row.totalValue?.plannedBudget} prefix={currencyId} />}
                      </div>
                    </td>
                    <td rowSpan={7} className={stickTotal ? 'textRight sticky sticky5 align-top noWrap' : 'textRight align-top noWrap'}>
                      {<FormatMoney value={row.totalValue?.actualSale} prefix={currencyId} />}
                    </td>
                    <td rowSpan={7} className={stickTotal ? 'textRight sticky sticky6 align-top' : 'textRight align-top'}>{<NumericFormat value={row.totalValue?.upt || 0} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
                    <td rowSpan={7} className={stickTotal ? 'textRight sticky sticky7 align-top' : 'textRight align-top'}>{<FormatMoney value={row.totalValue?.averagePrice} prefix={currencyId} decimalScale={1} />}</td>
                    <td rowSpan={7} className={stickTotal ? 'textRight sticky sticky8 align-top' : 'textRight align-top'}>{row.totalValue?.receiptNumber}</td>
                    <td rowSpan={7} className={stickTotal ? 'textRight sticky sticky9 align-top' : 'textRight align-top'}>{<NumericFormat value={row.totalValue?.vpt} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
                    <td rowSpan={7} className={stickTotal ? 'divideRight textRight sticky sticky10 align-top noWrap' : 'divideRight textRight align-top noWrap'}>{<FormatMoney value={row.totalValue?.budgetCalculated || 0} prefix={currencyId} />}</td>
                  </tr>
                  <tr>
                  {
                    months.map((mth, index) => {
                      const monthNumber = mth;
                      
                      const plannedBudget = row.monthValues[index]?.plannedBudget;
                      const saleAssistantBudget = row.monthValues[index]?.saleAssistantBudget;
                      const deltaPercentage = row.monthValues[index]?.deltaPercentage;
                      return (
                        <>
                          <td style={{"z-index": 0}}>Total</td>
                          <td className="textRight noWrap">
                            <div>
                              {<FormatMoney value={row.monthValues[index]?.totalBudget} prefix={currencyId} />}
                            </div>
                            <div className='tdSubBudget'>
                              Planned: <span className={plannedBudget > 0 ? 'textGreen' : 'textYellow'}>{<FormatMoney value={plannedBudget} prefix={currencyId} />}</span>
                            </div>
                            <div className='tdSubBudget'>
                              Client Advisor:&nbsp;
                              <span className={saleAssistantBudget > 0 ? 'textGreen' : 'textYellow'}>
                                {<Link
                                  className={'textUnderline'}
                                  onClick={() => {
                                    localStorage.setItem('posId', row?.posId);
                                    localStorage.setItem('selectedYear', selectedYear);
                                    localStorage.setItem('month', monthNumber);
                                    localStorage.setItem('posDs', row?.posDs);
                                    localStorage.setItem('backUrl', window.location.href);
                                    window.location.replace('/sa-kpi');
                                  }}
                                >
                                  <FormatMoney value={saleAssistantBudget} prefix={currencyId} />
                                </Link>}
                              </span>
                            </div>
                          </td>
                          <td className="textRight noWrap">
                            <FormatMoney value={row.monthValues[index]?.actualSales} prefix={currencyId} />
                          </td>
                          <td className="textRight">
                            {<FormatMoney value={row.monthValues[index]?.delta} prefix={currencyId} />}
                          </td>
                          <td className="textRight">
                            <span className={deltaPercentage > 0 ? 'textGreen' : 'textYellow'}>{<NumericFormat value={deltaPercentage} suffix={`%`} thousandSeparator=" " displayType="text" decimalScale={0} />}</span>
                          </td>
                          <td className="textRight divideRight noWrap">
                          {/* {<NumericFormat value={row.monthValues[index]?.budgetCalculated || 0} suffix={` ${currencyId}`} thousandSeparator=" " displayType="text" decimalScale={0} />} */}
                          {<Link
                            className={'textUnderline'}
                            onClick={() => {
                              localStorage.setItem('posId', row?.posId);
                              localStorage.setItem('selectedYear', selectedYear);
                              localStorage.setItem('month', monthNumber);
                              localStorage.setItem('posDs', row?.posDs);
                              localStorage.setItem('backUrl', window.location.href);
                              window.location.replace('/monthly-kpi');
                            }}
                          >
                            <FormatMoney value={row.monthValues[index]?.budgetCalculated || 0} prefix={currencyId} />
                          </Link>}
                          </td>
                        </>
                      );
                    })
                  }
                  </tr>
                  {
                    [1, 2, 3, 4, 5].map((weekNumber) => {
                      return (<tr>
                        {
                          months.map((monthNumber, index) => {
                            const week = row.monthValues[index]?.periodTarget[weekNumber - 1];
                            if (!week)
                              return (<><td>Week {weekNumber}</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></>);
                            const { actualSales, budget, delta, deltaPercentage, isEditable, period, planned } = week;
                            
                            
                            return (
                              <>
                                <td className="weekTd">Week {weekNumber}</td>
                                <td className='textRight'>
                                  <div>
                                    {isEditable ? (
                                      <FormatMoney 
                                        value={formData?.[row.posId]?.[period]?.budget || budget}
                                        prefix={currencyId}
                                        renderText={(value) =>
                                          <TextInput
                                            disabled={loading}
                                            posId={row.posId}
                                            month={monthNumber}
                                            period={period}
                                            fieldName="budget"
                                            value={value}
                                            onChange={handleChange}
                                          />
                                        }
                                      />
                                    ) : (
                                      <div style={{minWidth: '104px'}}><FormatMoney value={budget} prefix={currencyId} /></div>
                                    )}
                                    <div className='plannedBudget'>
                                      Planned:&nbsp;
                                      <Link
                                        className={planned == budget ? 'textGreen textUnderline' : 'textYellow textUnderline'}
                                        onClick={() => {
                                          localStorage.setItem('posId', row?.posId);
                                          localStorage.setItem('selectedYear', selectedYear);
                                          localStorage.setItem('month', monthNumber);
                                          localStorage.setItem('period', period);
                                          localStorage.setItem('weekNumber', weekNumber);
                                          localStorage.setItem('backUrl', '/');
                                          window.location.replace('/planned-sales');
                                        }}
                                      >
                                        <FormatMoney value={planned} prefix={currencyId} />
                                      </Link>
                                    </div>
                                  </div>
                                </td>
                                <td className='textRight'>
                                  <FormatMoney value={actualSales} prefix={currencyId} />
                                </td>
                                <td className='textRight'>
                                  <FormatMoney value={delta} prefix={currencyId} />
                                </td> 
                                <td className='textRight'>
                                  <span className={deltaPercentage > 0 ? 'textGreen' : 'textYellow'}>{<NumericFormat value={deltaPercentage} suffix={`%`} thousandSeparator=" " displayType="text" decimalScale={0} />}</span>
                                </td>
                                <td className='divideRight textRight'> </td>
                              </>
                            );
                          })
                        }
                      </tr>);
                    })
                  }
                </>
              );
            }

            )
          }
          <tr className="bgTableGrey totalRow">
            <td colSpan={3} className="sticky">Total</td>
            <td className={stickTotal ? 'textRight sticky sticky4 divideLeft' : 'textRight divideLeft'}>
              <div>{<FormatMoney value={targetSalesTotals(rows, 'totalValue.budget')} prefix={globalCurrencyId} />}</div>
              <div className="tdSubBudget">{<FormatMoney value={targetSalesTotals(rows, 'totalValue.plannedBudget')} prefix={globalCurrencyId} />}</div>
            </td>
            <td className={stickTotal ? 'textRight sticky sticky5' : 'textRight'}>{<FormatMoney value={targetSalesTotals(rows, 'totalValue.actualSale')} prefix={globalCurrencyId} />}</td>
            <td className={stickTotal ? 'textRight sticky sticky6' : 'textRight'}>{<NumericFormat value={targetSalesAverage(rows, 'totalValue.upt')} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
            <td className={stickTotal ? 'textRight sticky sticky7' : 'textRight'}>{<FormatMoney value={targetSalesAverage(rows, 'totalValue.averagePrice')} prefix={globalCurrencyId} decimalScale={1} />}</td>
            <td className={stickTotal ? 'textRight sticky sticky8' : 'textRight'}>{targetSalesTotals(rows, 'totalValue.receiptNumber')}</td>
            <td className={stickTotal ? 'textRight sticky sticky9' : 'textRight'}>{<NumericFormat value={targetSalesAverage(rows, 'totalValue.vpt')} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
            <td className={stickTotal ? 'divideRight textRight sticky sticky10' : 'divideRight textRight'}>{<FormatMoney value={targetSalesTotals(rows, 'totalValue.budgetCalculated')} prefix={globalCurrencyId} />}</td>
            {
              monthsHeader.map((month) => {
                return (
                  <>
                    <td></td>
                    <td className="textRight"> 
                      <div>{<FormatMoney value={targetSalesTotalsV3(rows, month, `budget`)} prefix={globalCurrencyId} />}</div>
                      <div className="tdSubBudget">{<FormatMoney value={targetSalesTotalsV3(rows, month, `planned`)} prefix={globalCurrencyId} />}</div>
                    </td>
                    <td className="textRight">{<FormatMoney value={targetSalesTotalsV3(rows, month, `actualSales`)} prefix={globalCurrencyId} />}</td>
                    <td className="textRight">{<FormatMoney value={targetSalesTotalsV3(rows, month, `delta`)} prefix={globalCurrencyId} />}</td>
                    <td className="textRight">{<NumericFormat value={targetSalesAverageV3(rows, month, `deltaPercentage`)} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
                    <td className="textRight"></td>
                  </>
                );
              })
            }
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default TargetTable;